/**
 * Die tool-eigenen Zusatzfelder eines JTL-Lieferanten schreiben (Task 11, Befund 15 - Option B,
 * entschieden 2026-08-11).
 *
 * ============================== WARUM EIN ZWEITES LIEFERANTEN-MODUL ==============================
 *
 * `api/lieferanten.ts` ist der LESEweg (`lieferantLaden`, zugleich der SSE-Nachlader der Entitaet).
 * Sein Kopfkommentar haelt seit Fix-Runde 1 ausdruecklich fest: "`PATCH .../extras` ist hier bewusst
 * NICHT nachgebaut: es gibt heute keine Flaeche, die Lieferanten-Zusatzfelder schreibt". Diese
 * Flaeche gibt es seit Task 11 - der Schreibweg entsteht deshalb hier, in einem eigenen Modul, statt
 * den Lesepfad um eine Funktion zu erweitern, die mit dem Nachlader nichts zu tun hat.
 *
 * ============================== GEMESSEN, NICHT ANGENOMMEN ==============================
 *
 * Jede der folgenden Angaben stammt aus dem Backend-Quelltext dieses Arbeitsbaums, nicht aus dem
 * Task-Brief (Projektregel: "Miss es, statt es anzunehmen - auch wenn es im Auftrag steht"):
 *
 *   Route     `PATCH /api/suppliers/:kLieferant/extras`, Recht `manage_suppliers`
 *             (`backend/src/http/endpunktMatrix.ts:106`). Es gibt darunter GENAU ZWEI Endpunkte -
 *             kein `GET /api/suppliers` (Liste), kein POST, kein DELETE (Zeilen 105-106).
 *   Koerper   `{ rowVersion, felder }` - dieselbe Form wie die Feldgruppen-PATCH der Bestellungen
 *             (`backend/src/http/routes/suppliers.ts:60-77`). `felder` muss ein nicht-leeres Objekt
 *             sein, sonst 400 (`FELDER_ERFORDERLICH_MELDUNG`, ebenda).
 *   rowVersion `null` ist ein GUELTIGER, eigenstaendiger Wert und keine Auslassung: er heisst
 *             "ich glaube, es gibt die Zeile noch nicht" und loest serverseitig ein
 *             `INSERT ... ON CONFLICT DO NOTHING` aus; existiert die Zeile doch, kommt ein 409 mit
 *             dem aktuellen Stand statt einer stillen Ueberschreibung
 *             (`backend/src/domaene/lieferanten.ts#schreibeSupplierExtras`, Fallunterscheidung im
 *             Kopfkommentar dort). Deshalb `number | null` und NICHT `number | undefined`.
 *   Felder    die sechs Spalten aus `SUPPLIER_FELDER`
 *             (`backend/src/domaene/schreibbareFelder.ts:78-88`) - in SNAKE_CASE.
 *
 * ============================== DIE ZWEI SCHREIBWEISEN, EINE NAHT ==============================
 *
 * DIE FALLE DIESER ROUTE (gemessen, beide Richtungen): LESEN und SCHREIBEN benutzen fuer dieselben
 * Spalten VERSCHIEDENE Namen.
 *
 *   Lesen   `GET /api/suppliers/:kLieferant` schickt die Zeile durch `oeffentlich()`
 *           (`backend/src/domaene/oeffentlich.ts`), das jeden Spaltennamen mechanisch nach camelCase
 *           setzt: `short_name` -> `shortName`, `payment_plan` -> `paymentPlan`,
 *           `seasonal_rules` -> `seasonalRules`. `eur`, `logo` und `notes` sind einsilbig und
 *           bleiben dabei unveraendert - genau die drei, an denen der Fehler NICHT auffiele.
 *   Schreiben `schreibeSupplierExtras` prueft JEDEN Feldnamen gegen `SUPPLIER_FELDER`, also gegen die
 *           SNAKE_CASE-Spaltennamen, und wirft sonst `FeldNichtInGruppeError` (400).
 *
 * Ein Formular, das seine gelesenen Schluessel unveraendert zurueckschreibt, traefe also bei drei von
 * sechs Feldern ins Leere. `SUPPLIER_EXTRAS_FELDER` unten fuehrt beide Namen je Feld nebeneinander;
 * `ExtrasFormular.tsx` ist der einzige Verbraucher und liest AUS `lesename`, schreibt IN
 * `schreibname`.
 *
 * ============================== DER RIEGEL (Task 5a, gleiche Bauform) ==============================
 *
 * `bestellungAendern` (`api/bestellungen.ts`) scheitert absichtlich laut, wenn ein Feld keiner
 * Feldgruppe angehoert (`FeldOhneGruppeFehler`, RIEGEL 2) - statt das Feld stillschweigend
 * wegzulassen. Hier gilt dasselbe: ein Feldname ausserhalb der sechs ist ein Programmierfehler und
 * wird SOFORT geworfen, nicht an den Server geschickt, der ihn ohnehin mit 400 abwiese. Der
 * Unterschied ist die Diagnose: hier steht der falsche Name samt der sechs erlaubten in der Meldung,
 * dort nur "Feld nicht in Gruppe" ueber die Netzwerkkonsole.
 */
import { anfrage } from "./basis.ts";
import { BASISPFAD } from "./lieferanten.ts";

/** Ein Zusatzfeld in beiden Schreibweisen - siehe Kopfkommentar, "DIE ZWEI SCHREIBWEISEN". */
export interface SupplierExtrasFeld {
  /** Spaltenname, wie ihn `PATCH .../extras` verlangt (snake_case, `SUPPLIER_FELDER`). */
  readonly schreibname: string;
  /** Schluessel, unter dem `GET /api/suppliers/:kLieferant` den Wert liefert (camelCase). */
  readonly lesename: string;
}

/**
 * Die vier Zusatzfelder, die die Oberflaeche pflegt (Teilmenge von `SUPPLIER_FELDER`,
 * `backend/src/domaene/schreibbareFelder.ts`).
 *
 * `terms` fehlt hier bewusst und ist KEIN Vergessen: Zahlungsziel und Skonto kommen live aus JTL
 * (`tlieferant.nZahlungsziel`/`fSkonto`), ein Tool-Feld daneben waere ein zweiter, konkurrierender
 * Wahrheitsort - die Begruendung steht woertlich am Backend-Konstantenkopf.
 *
 * `payment_template` stand hier bis Migration 039 (Fixrunde 1, Zahlungsziele strukturiert,
 * 2026-09-07) - die Spalte ist geloescht, die Zuordnung Lieferant -> Vorlage liegt seither in
 * `supplier_payment_terms` (Freigabeliste, mehrere Optionen statt genau einer) und wird ueber einen
 * eigenen, spaeter gebauten Endpunkt gepflegt, nicht mehr ueber dieses PATCH. Ohne diese Streichung
 * schickte JEDES Speichern der Zusatzfelder das Feld unveraendert mit und scheiterte an
 * `FeldNichtInGruppeError` (400) - unabhaengig davon, welches Feld der Nutzer tatsaechlich geaendert
 * hatte, siehe `ExtrasFormular.tsx#felderAusEntwurf` (baut den Koerper ueber ALLE Felder, nicht nur
 * die geaenderten).
 *
 * `payment_plan` und `seasonal_rules` stehen seit dem 01.10.2026 NICHT mehr hier (Seite
 * "Zahlungsvorlagen"): der Zahlungsplan (Raten | JSON) hat im Backend keinen Leser mehr, die
 * Saisonregeln sind abgeschaltet. Das Backend behaelt beide Spalten in seiner Schreibliste und in
 * der Datenbank; ein PATCH, der sie nicht nennt, laesst ihre Werte unberuehrt. Lesbar bleiben sie
 * ueber den flachen Datensatz (`paymentPlan`, `seasonalRules`), die Oberflaeche zeigt sie nicht mehr.
 */
export const SUPPLIER_EXTRAS_FELDER: readonly SupplierExtrasFeld[] = Object.freeze([
  { schreibname: "eur", lesename: "eur" },
  { schreibname: "short_name", lesename: "shortName" },
  { schreibname: "logo", lesename: "logo" },
  { schreibname: "notes", lesename: "notes" },
] as const);

/** Die Schreibnamen als blanke Liste - der Riegel unten und die Tests pruefen dagegen. */
export const SUPPLIER_EXTRAS_SCHREIBNAMEN: readonly string[] = Object.freeze(
  SUPPLIER_EXTRAS_FELDER.map((feld) => feld.schreibname),
);

/**
 * Ein Feldname, den `PATCH .../extras` nicht kennt - dieselbe Absicht wie `FeldOhneGruppeFehler`
 * in `api/bestellungen.ts`: laut scheitern statt still weglassen.
 */
export class FeldNichtSchreibbarFehler extends Error {
  readonly felder: readonly string[];

  constructor(felder: readonly string[]) {
    /*
     * FIX-RUNDE 1 (2026-08-11): der Fehler wird aus ZWEI Gruenden geworfen - unbekannter Feldname
     * ODER gar kein Feld (`extrasSchreiben` unten, `namen.length === 0`; der Server antwortet auf
     * ein leeres `felder` mit 400). Im zweiten Fall ist `felder` LEER, und die Meldung begann
     * bisher mit einer leeren Aufzaehlung: "extrasSchreiben:  ist kein schreibbares
     * supplier_extras-Feld." - ausgerechnet in dem Modul, das seine gute Diagnose gegen den blossen
     * 400 des Servers reklamiert. Beide Faelle bekommen deshalb ihren eigenen Satz; die Liste der
     * erlaubten Spalten steht in beiden, weil sie in beiden weiterhilft.
     */
    const anlass =
      felder.length === 0
        ? "extrasSchreiben wurde ohne ein einziges Feld aufgerufen - das ist nie eine gueltige " +
          "Absicht (der Server antwortet darauf mit 400)."
        : `extrasSchreiben: ${felder.map((f) => `"${f}"`).join(", ")} ist kein schreibbares ` +
          "supplier_extras-Feld.";
    super(
      `${anlass} Erlaubt sind ausschliesslich: ` +
        `${SUPPLIER_EXTRAS_SCHREIBNAMEN.join(", ")} (backend/src/domaene/schreibbareFelder.ts). ` +
        "Achtung: der LESEweg liefert dieselben Spalten in camelCase - siehe SUPPLIER_EXTRAS_FELDER.",
    );
    this.name = "FeldNichtSchreibbarFehler";
    this.felder = Object.freeze([...felder]);
  }
}

/**
 * Schreibt Zusatzfelder EINES Lieferanten. `kLieferant` ist die JTL-Kennung als Zeichenkette (genau
 * die `id`, die `lieferantLaden` liefert - `String(kLieferant)`).
 *
 * Wirft `FeldNichtSchreibbarFehler` bei einem unbekannten Feldnamen und bei einem LEEREN Objekt: der
 * Server antwortet darauf mit 400, ein Aufruf ohne Feld ist also nie eine gueltige Absicht. Bei 409
 * wirft `anfrage()` einen `KonfliktFehler`, dessen `aktuellerStand` den Serverstand bereits
 * mitbringt - `ExtrasFormular.tsx` zeigt ihn an, ohne ein zweites Mal zu laden.
 */
export async function extrasSchreiben(
  kLieferant: string,
  felder: Record<string, unknown>,
  rowVersion: number | null,
): Promise<Record<string, unknown>> {
  const namen = Object.keys(felder);
  const unbekannt = namen.filter((name) => !SUPPLIER_EXTRAS_SCHREIBNAMEN.includes(name));
  if (namen.length === 0 || unbekannt.length > 0) {
    throw new FeldNichtSchreibbarFehler(unbekannt);
  }
  return anfrage<Record<string, unknown>>(
    `${BASISPFAD}/${encodeURIComponent(kLieferant)}/extras`,
    { methode: "PATCH", koerper: { rowVersion, felder } },
  );
}
